import { Loader2 } from 'lucide-react';
import { portalDs, PortalDsAlert } from 'src/components/portal-design-system';
import { FilterBar, MetricCard, MetricCardGrid } from 'src/components/shared';
import { cn } from 'src/lib/utils';
import { PortalSyncNotConfiguredBanner } from './components/portal-sync-not-configured-banner';
import { UsageLimitConfigEditor } from './components/usage-limit-config-editor';
import { UsersTable } from './components/users-table';
import {
  useGetPortalMetricsQuery,
  useGetPortalUsageLimitConfigQuery,
} from './endpoints/portal-users.endpoints';
import { useFlowUsers } from './hooks/use-flow-users';
import { mapPortalMetrics } from './utils/portal-users.mappers';
import type { StatusFilter, SubscriptionFilter } from './types/users.types';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Blocked' },
] as const;

const SUBSCRIPTION_FILTER_OPTIONS = [
  { value: 'all', label: 'All tiers' },
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'media_partner', label: 'Media partner' },
  { value: 'internal', label: 'Internal' },
] as const;

export function UsersTab() {
  const {
    users,
    totalCount,
    page,
    pageSize,
    setPage,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    subscriptionFilter,
    setSubscriptionFilter,
    isLoading,
    isError,
    isPortalSyncNotConfigured,
    applyBlockAction,
    applyUnblockAction,
    saveQuotaOverride,
    saveSubscriptionTier,
    actionsDisabled,
  } = useFlowUsers();

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useGetPortalMetricsQuery();

  const { data: usageLimitConfig } = useGetPortalUsageLimitConfigQuery();

  const metrics = metricsData ? mapPortalMetrics(metricsData) : null;

  return (
    <div className={cn(portalDs.page, 'h-full overflow-y-auto px-6 py-8 sm:px-10')}>
      {isMetricsLoading ? (
        <div className="mb-6 flex items-center gap-2 text-[#5E6278]">
          <Loader2
            className="size-4 animate-spin"
            aria-hidden
          />
          <span className={portalDs.caption}>
            Loading dashboard metrics…
          </span>
        </div>
      ) : isMetricsError && !isPortalSyncNotConfigured ? (
        <PortalDsAlert
          variant="danger"
          title="Could not load dashboard metrics"
          className="mb-6"
        >
          Metrics are temporarily unavailable. Try refreshing the page.
        </PortalDsAlert>
      ) : metrics ? (
        <MetricCardGrid>
          <MetricCard
            value={metrics.newUsersLast30d}
            label="New users (last 30 days)"
            delta={{
              value: metrics.newUsersDelta,
              direction: metrics.newUsersDeltaDirection,
            }}
          />
          <MetricCard
            value={metrics.creativesGeneratedLast30d}
            label="Creatives generated (last 30 days)"
          />
          <MetricCard
            value={metrics.brandsIngestedLast30d}
            label="Brands ingested (last 30 days)"
          />
        </MetricCardGrid>
      ) : null}

      <UsageLimitConfigEditor disabled={actionsDisabled} />

      <FilterBar
        searchValue={search}
        searchPlaceholder="Search users by name or email"
        onSearchChange={setSearch}
        selects={[
          {
            id: 'status',
            label: 'Status',
            value: statusFilter,
            options: [...STATUS_FILTER_OPTIONS],
            onChange: (value) => setStatusFilter(value as StatusFilter),
          },
          {
            id: 'subscription',
            label: 'Subscription',
            value: subscriptionFilter,
            options: [...SUBSCRIPTION_FILTER_OPTIONS],
            onChange: (value) => setSubscriptionFilter(value as SubscriptionFilter),
          },
        ]}
      />

      {isPortalSyncNotConfigured ? (
        <PortalSyncNotConfiguredBanner />
      ) : isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-[#5E6278]">
          <Loader2
            className="size-5 animate-spin"
            aria-hidden
          />
          <span>
            Loading users…
          </span>
        </div>
      ) : isError ? (
        <PortalDsAlert
          variant="danger"
          title="Could not load users"
        >
          User data is temporarily unavailable. Try refreshing the page.
        </PortalDsAlert>
      ) : (
        <UsersTable
          users={users}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          globalCreativeQuota={usageLimitConfig?.monthly_creative_limit}
          onPageChange={setPage}
          onBlock={(userId) => void applyBlockAction(userId)}
          onUnblock={(userId) => void applyUnblockAction(userId)}
          onSaveQuotaOverride={saveQuotaOverride}
          onSaveSubscriptionTier={saveSubscriptionTier}
          actionsDisabled={actionsDisabled}
        />
      )}
    </div>
  );
}
